import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-shield-keyhole-line',
    title: 'Advanced Security',
    description:
      'Our applications are built with security at the core. Every feature is designed to protect user data and ensure privacy across all iOS devices.',
  },
  {
    icon: 'ri-speed-line',
    title: 'High Performance',
    description:
      'Optimized for iOS architecture, our apps deliver lightning-fast performance without draining battery life or consuming excessive resources.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Intuitive UX',
    description:
      'Clean, minimal interfaces that feel native to iOS. We follow Apple\'s Human Interface Guidelines to deliver seamless user experiences.',
  },
  {
    icon: 'ri-global-line',
    title: 'Global VPN Network',
    description:
      'Our VPN solution connects users to a worldwide network of secure servers, ensuring fast and private browsing from anywhere on the globe.',
  },
  {
    icon: 'ri-delete-bin-6-line',
    title: 'Smart Cleaning',
    description:
      'Intelligent algorithms detect and remove junk files, duplicate photos, and unnecessary caches to keep iOS devices running at peak condition.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Regular Updates',
    description:
      'We continuously improve our products with regular updates, new features, and security patches to stay ahead of emerging threats.',
  },
];

function FeatureCard({
  icon,
  title,
  description,
  index,
}: {
  icon: string;
  title: string;
  description: string;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group bg-white rounded-2xl p-7 border border-gray-100 hover:border-sky-100 hover:shadow-lg transition-all duration-500 cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s, box-shadow 0.3s ease, border-color 0.3s ease`,
      }}
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-sky-50 group-hover:bg-sky-100 transition-colors duration-300 mb-5">
        <i className={`${icon} text-sky-500 text-xl`} />
      </div>
      <h3 className="text-gray-900 font-bold text-lg mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>
        {title}
      </h3>
      <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

export default function FeaturesSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="py-24 md:py-32 bg-gray-50/60">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={titleRef}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div>
            <span className="text-xs font-semibold tracking-[3px] uppercase text-sky-500 mb-3 block">
              What We Do
            </span>
            <h2
              className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              Built for the
              <br />
              iOS Ecosystem
            </h2>
          </div>
          <p className="text-gray-500 text-base leading-relaxed max-w-sm md:text-right">
            Every feature we build is crafted with precision, performance, and the iOS user in mind.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <FeatureCard key={f.title} {...f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
